import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import {
	AgXToneMapping,
	PCFShadowMap,
	SRGBColorSpace,
	VSMShadowMap,
} from "three";
import type { ViewerRuntimeProfile } from "./viewerConfig";
import { TONE_MAPPING_EXPOSURE_COMPENSATION } from "./viewerConfig";

interface RenderControllerProps {
	runtimeProfile: ViewerRuntimeProfile;
}

/**
 * Configures the R3F renderer to match the original Viewer3D settings:
 * - AgX tone mapping (gentler highlight rolloff on warm wood than
 *   ACES; exposure compensated via TONE_MAPPING_EXPOSURE_COMPENSATION)
 * - sRGB output color space
 * - Quality-tier exposure and pixel ratio
 * - PCF shadow maps
 *
 * We rely on R3F's Canvas `frameloop="demand"` + invalidate() for the
 * render loop. The old dual-path (renderer vs composer during interaction)
 * is handled automatically by @react-three/postprocessing which integrates
 * into R3F's render pipeline.
 */
export function RenderController({ runtimeProfile }: RenderControllerProps) {
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);

	useEffect(() => {
		gl.toneMapping = AgXToneMapping;
		gl.toneMappingExposure =
			runtimeProfile.toneMappingExposure * TONE_MAPPING_EXPOSURE_COMPENSATION;
		gl.outputColorSpace = SRGBColorSpace;
		gl.setPixelRatio(
			Math.min(window.devicePixelRatio || 1, runtimeProfile.pixelRatioCap),
		);
		// PCFSoftShadowMap was deprecated in three r183 (falls back to
		// plain PCF), and drei's PCSS <SoftShadows> patch no longer
		// compiles against the r183 shadow pipeline. VSM is the
		// remaining soft-penumbra path: the key light's shadow.radius /
		// blurSamples (set in SceneLighting) control the blur.
		gl.shadowMap.type = runtimeProfile.softShadows
			? VSMShadowMap
			: PCFShadowMap;
		gl.shadowMap.needsUpdate = true;
		invalidate();
	}, [gl, runtimeProfile, invalidate]);

	return null;
}
